import { Link } from 'react-router';
import type { MatchGroup, MatchGroupLine } from '../../../hooks/useMatchGroups';
// #1305 — same effective (kolli-folded) quantity rule the hook's
// aggregate pass uses, so the §3 split buckets folded lines correctly.
import { effectiveLineQty } from '../../../hooks/useMatchGroups';
import { buildPriceIndex, lookupPrice } from './priceIndex';
// #1289 C2 — single de-DE EUR formatter shared with MatchGraph + feed.
import { eurFmt } from './deliveryGroupSummary';
// #1289 C3 — shared operator-resolved exclusion (feed + tiles agree).
import { isEventResolved, isOperatorResolved, rederiveLinePairs } from './resolvedExclusion';
import './FilterBar.css';

export interface DashboardTotals {
  /** Σ settlement_event.total_amount across fully matched pairs PLUS
   *  the matched-line portion of partial groups (clean line pairs +
   *  the overlapping quantity of qty_mismatch pairs, priced at the
   *  settlement-side unit_price). This is the "green money" baseline
   *  the user reads as "abgeglichen" — the rest of each partial GS's
   *  euros lands in the two fehlt tiles, so Vollständig + Gutschrift-
   *  fehlt + Lieferschein-fehlt is a near-complete coverage of every
   *  euro that flowed through the month (modulo non-goods document
   *  fees like Kommission / Skonto / MWSt that we deliberately skip). */
  matchedMoney: number;
  /** Lieferschein-fehlt aggregate: orphan_settlement line amounts
   *  PLUS unmatched GOODS-side GS lines in partial groups
   *  ("credited without delivery proof" at the line level)
   *  PLUS GS-overage portions of quantity_mismatch lines in partial
   *  groups (settlement says we credited 100 kg, delivery only proves
   *  90 kg → 10 kg × unit_price is "credited without proof"). Non-
   *  goods unmatched settlement lines (Kommission / Skonto / Steuern)
   *  are intentionally skipped — same skip that keeps
   *  ``deriveGroupStatus`` from flipping a Kommission-only diff to
   *  partial. */
  lieferscheinFehltMoney: number;
  /** Gutschrift-fehlt aggregate: orphan_delivery estimated value
   *  PLUS unmatched LS lines in partial groups ("delivered but not
   *  credited" at the line level)
   *  PLUS LS-overage portions of quantity_mismatch lines (delivery
   *  says 100 kg, settlement only credits 90 kg → 10 kg × unit_price
   *  is owed to the farmer). Priced via the shared fuzzy-tier index
   *  for unmatched lines and via the paired settlement line's
   *  unit_price for qty-mismatch deltas. */
  gutschriftFehltMoney: number;
  /** How many lines contributed at all to ``gutschriftFehltMoney``
   *  (orphan_delivery lines + partial-group unmatched LS + partial-
   *  group qty_mismatch with LS overage) that actually found a price
   *  anchor. Drives the "kein Preisanker" fallback hint. */
  gsFehltCovered: number;
  /** Total LS-side line count counted toward Gutschrift-fehlt
   *  (denominator for the coverage hint). */
  gsFehltLines: number;
  /** #2138 — the ``id``s of the groups with a genuine GS-side gap
   *  ("delivered but not credited"): every ``orphan_delivery`` plus each
   *  ``partial`` carrying an unmatched LS line, a fully-orphan LS event, or
   *  an LS-overage quantity mismatch (``dlQty > slQtySum``). Tagged off the
   *  gap itself, NOT its price — an unpriced gap (amount-only line, no price
   *  anchor) still belongs in Y; the sentence drops the money clause when
   *  ``gsFehltCovered === 0``, exactly as the tile prints "kein Preisanker".
   *  A pure OVER-credit partial (``slQtySum > dlQty`` → Lieferschein-fehlt
   *  only) is DELIBERATELY absent: it is not a missing Gutschrift, so it must
   *  not count toward the summary sentence's Y. FilterBar derives
   *  ``Y = orphan_delivery + (partials ∩ gsFehltGroupIds)`` so Y and Z
   *  describe one population — ``Y > 0 ⟺ Z > 0`` whenever the gap is priced
   *  (both 0 when it isn't). One source of truth: the set is populated by
   *  the SAME code that books the money, so the two cannot drift. */
  gsFehltGroupIds: Set<string>;
  /** #1311 — Abweichung tile money. Every euro the ``partial`` branch
   *  of ``computeDashboardTotals`` routes into EITHER fehlt bucket
   *  (GS-side gaps at face value + LS-side gaps fuzzy-priced),
   *  captured as a snapshot-delta of both fehlt accumulators around the
   *  ``case 'partial'`` body. This is a per-category LENS, NOT an
   *  additive bucket: the same euros also remain in
   *  ``gutschriftFehltMoney`` / ``lieferscheinFehltMoney`` (the money
   *  folding stays — fehlt tooltips already disclose the overlap), so
   *  Vollständig + the two fehlt tiles still cover the month without
   *  double counting. Rendered with the "ca." estimate prefix because
   *  the LS-side portion is fuzzy-priced. */
  abweichungMoney: number;
}

function readTotalAmount(ev: MatchGroup['settlementEvent']): number | null {
  if (!ev) return null;
  const raw = (ev as Record<string, unknown>).total_amount;
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

interface TileProps {
  // #1511 — ``total`` accent for the leading Gesamt tile: a neutral-blue
  // primary cue distinct from the per-status severity accents and from
  // the muted ``neutral`` zero-state.
  accent: 'ok' | 'warn' | 'alarm' | 'neutral' | 'total';
  label: string;
  count: number;
  money?: { value: number; estimate?: boolean } | null;
  moneyEmpty?: string;
  moneyTitle?: string;
  // #1311 — tiles are the status filter control. ``isActive`` marks the
  // tile whose status is the current filter (accent-tinted +
  // aria-pressed); ``onClick`` toggles the filter on/off.
  isActive: boolean;
  onClick: () => void;
}

function Tile({ accent, label, count, money, moneyEmpty, moneyTitle, isActive, onClick }: TileProps) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      className={
        `filter-bar__tile filter-bar__tile--${accent}` +
        (isActive ? ' filter-bar__tile--active' : '')
      }
    >
      <div className="filter-bar__tile-label">{label}</div>
      <div className="filter-bar__tile-data">
        <span className="filter-bar__tile-count">{count}</span>
        {(money || moneyEmpty) && (
          <span className="filter-bar__tile-sep" aria-hidden="true">·</span>
        )}
        {money ? (
          <span className="filter-bar__tile-money" title={moneyTitle}>
            {money.estimate && <span className="filter-bar__tile-money-prefix">ca. </span>}
            {eurFmt.format(money.value)}
          </span>
        ) : moneyEmpty ? (
          <span className="filter-bar__tile-money-empty">{moneyEmpty}</span>
        ) : null}
      </div>
    </button>
  );
}

export function computeDashboardTotals(groups: MatchGroup[]): DashboardTotals {
  const priceIndex = buildPriceIndex(groups);

  let matchedMoney = 0;
  let lieferscheinFehltMoney = 0;
  let gutschriftFehltMoney = 0;
  let gsFehltCovered = 0;
  let gsFehltLines = 0;
  // #2138 — group ids that fed a GS-side gap, so the FilterBar summary can
  // derive Y as exactly Z's population (see the DashboardTotals doc).
  const gsFehltGroupIds = new Set<string>();
  // #1311 — Abweichung lens: the partial-branch flow into either fehlt
  // bucket, accumulated as a snapshot-delta around ``case 'partial'``.
  let abweichungMoney = 0;

  // #2138 — ``groupId`` tags the gap with its owning group. Recorded even
  // when the line has no price anchor (matching ``gsFehltLines`` semantics):
  // a genuine "delivered but not credited" gap belongs in Y regardless of
  // whether we could price it — the sentence drops the money clause when
  // ``gsFehltCovered === 0``, exactly as the tile prints "kein Preisanker".
  //
  // HOF-215 — ``lookupNames`` overrides which product text is asked of the index.
  // Tier-A anchors are keyed by the DELIVERY line's product name (that is the
  // side the orphan-LS lookup uses), so a SETTLEMENT line handed here would miss
  // them whenever the two documents word the product differently — which the
  // matcher explicitly permits, since an exact quantity reconciliation pairs
  // even when both product and packaging strings differ. Callers pass the paired
  // delivery names first and the line's own name as the fallback.
  const addGutschriftFehltLine = (
    line: MatchGroupLine,
    groupId: string,
    lookupNames?: string[],
  ) => {
    gsFehltLines++;
    gsFehltGroupIds.add(groupId);
    if (line.quantity == null) return;
    let price: number | null = null;
    for (const name of lookupNames ?? [line.productName]) {
      price = lookupPrice(priceIndex, name);
      if (price != null) break;
    }
    if (price == null) return;
    gutschriftFehltMoney += line.quantity * price;
    gsFehltCovered++;
  };

  for (const g of groups) {
    switch (g.status) {
      case 'matched': {
        // #1291 codex P1 — a reconciled N:1 group spans several GS
        // documents; the primary (lex-smallest id) can be the NEGATIVE
        // correction, so summing only ``g.settlementEvent`` would book
        // −2 160 € for Wiesenhof instead of the net 8 640 €. Sum every
        // settlement event; fall back to the primary when the list is
        // empty (legacy facts / fixtures) — identical for 1-GS groups
        // because ``allSettlementEvents[0]`` IS the primary.
        const events =
          g.allSettlementEvents.length > 0
            ? g.allSettlementEvents
            : g.settlementEvent
              ? [g.settlementEvent]
              : [];
        for (const ev of events) {
          const total = readTotalAmount(ev);
          if (total != null) matchedMoney += total;
        }
        break;
      }
      case 'orphan_settlement': {
        // Use the document-level ``total_amount`` so the dashboard
        // figure matches the per-tile "Gutschriftbetrag X €" shown on
        // the canvas missing-LS stub (same source). Summing per-line
        // ``amount`` values diverged from ``total_amount`` whenever the
        // extractor stored fees / discounts on lines but tracked the
        // gross only at the document level — the user spotted the
        // mismatch when stub-sum (44.093 €) didn't equal the
        // dashboard (39.925 €).
        //
        // #1345: sum EVERY settlement event in the group, not just the
        // primary. The #1345 zero-reconciliation re-bucket routes
        // multi-GS components (2+ GS sharing one delivery, nothing
        // reconciled) here as well as the classic single-GS orphan, so
        // reading only ``g.settlementEvent`` would drop the 2nd+ GS's
        // € (the old ``partial`` path summed all of them via
        // ``allSettlementEvents``). Mirror the ``matched`` branch:
        // iterate ``allSettlementEvents``, fall back to the primary for
        // legacy facts / fixtures with an empty list (identical to the
        // single-GS case where ``allSettlementEvents[0]`` IS the
        // primary).
        const events =
          g.allSettlementEvents.length > 0
            ? g.allSettlementEvents
            : g.settlementEvent
              ? [g.settlementEvent]
              : [];
        for (const ev of events) {
          const total = readTotalAmount(ev);
          if (total != null && total > 0) lieferscheinFehltMoney += total;
        }
        break;
      }
      case 'orphan_delivery': {
        for (const dl of g.deliveryLines) addGutschriftFehltLine(dl, g.id);
        break;
      }
      case 'partial': {
        // A partial group is an "incompletely matched" component.
        // First, classify each event in the component as
        // "fully-orphan-within-group" (no lines from this event paired
        // with anything on the other side) or "has-matched-lines". A
        // GS in a 1:N component can land in the fully-orphan bucket
        // while its siblings match cleanly — e.g. LF99990014 paired
        // with one GS but another GS in the same component has no
        // matching LS lines. The fully-orphan side gets the
        // event's *full* ``total_amount`` attributed to the
        // corresponding fehlt (mirroring the orphan_settlement /
        // orphan_delivery branches above) so document-level overhead
        // (Kommission, MWSt) isn't dropped on the floor.
        //
        // Has-matched-lines events fall back to per-line classification:
        //   - matched line              → Vollständig (its full amount)
        //   - qty_mismatch overlap      → Vollständig (min(qty) × price)
        //   - qty_mismatch GS overage   → Lieferschein fehlt
        //   - qty_mismatch LS overage   → Gutschrift fehlt
        //   - unmatched GS goods line   → Lieferschein fehlt
        //   - unmatched LS line         → Gutschrift fehlt (fuzzy-priced)
        //   - unmatched non-goods GS    → skipped (Kommission / Skonto /
        //                                 MWSt are document-level
        //                                 overhead, not a "missing
        //                                 Lieferschein")
        //
        // #1311 — snapshot both fehlt accumulators before the partial
        // body runs; the per-direction delta after the body is exactly
        // this group's Abweichung contribution (money folding into the
        // fehlt buckets stays; abweichungMoney is the per-category lens).
        const lsBefore = lieferscheinFehltMoney;
        const gsBefore = gutschriftFehltMoney;

        // Index lines by their parent event so we can ask "does GS X
        // have any matched (or qty_mismatch) lines?".
        const slByEvent = new Map<string, MatchGroupLine[]>();
        for (const sl of g.settlementLines) {
          const arr = slByEvent.get(sl.eventId) ?? [];
          arr.push(sl);
          slByEvent.set(sl.eventId, arr);
        }
        const dlByEvent = new Map<string, MatchGroupLine[]>();
        for (const dl of g.deliveryLines) {
          const arr = dlByEvent.get(dl.eventId) ?? [];
          arr.push(dl);
          dlByEvent.set(dl.eventId, arr);
        }
        const fullyOrphanSettlementEvents = new Set<string>();
        const fullyOrphanDeliveryEvents = new Set<string>();

        for (const seEvent of g.allSettlementEvents) {
          const eid = String(
            (seEvent as Record<string, unknown>).entity_id ?? '',
          );
          if (!eid) continue;
          const lines = slByEvent.get(eid) ?? [];
          // HOF-215 — ``price_missing`` counts as PAIRED here (codex round 1,
          // P1). It means "this line has a counterpart and was credited at
          // nothing", so an event carrying one is not orphaned: attributing its
          // whole ``total_amount`` to a fehlt bucket would report a five-figure
          // loss where the real one is one line's worth.
          const hasAnyMatched = lines.some((l) => l.matchStatus !== 'unmatched');
          if (hasAnyMatched) continue;
          fullyOrphanSettlementEvents.add(eid);
          const total = readTotalAmount(seEvent);
          if (total != null && total > 0) {
            lieferscheinFehltMoney += total;
          }
        }
        for (const deEvent of g.allDeliveryEvents) {
          const eid = String(
            (deEvent as Record<string, unknown>).entity_id ?? '',
          );
          if (!eid) continue;
          const lines = dlByEvent.get(eid) ?? [];
          // Same as the settlement side above. On HOF-90's 619 + 605 -> 1224
          // exemplar the SHARED delivery line takes ``price_missing`` from the
          // worst-of ranking, so this is the branch that would have declared the
          // whole Lieferschein orphaned.
          const hasAnyMatched = lines.some((l) => l.matchStatus !== 'unmatched');
          if (hasAnyMatched) continue;
          fullyOrphanDeliveryEvents.add(eid);
          // Mirror the orphan_delivery branch: price each line via the
          // shared fuzzy-tier index. addGutschriftFehltLine handles the
          // "no price anchor" fallback by leaving covered/money unchanged
          // but still counting the line.
          for (const dl of lines) {
            addGutschriftFehltLine(dl, g.id);
          }
        }

        // (1) Unmatched LS lines on events that have *some* matched
        // lines → Gutschrift fehlt. Skip events already attributed
        // wholesale via the fully-orphan path above.
        for (const dl of g.deliveryLines) {
          if (dl.matchStatus !== 'unmatched') continue;
          if (fullyOrphanDeliveryEvents.has(dl.eventId)) continue;
          addGutschriftFehltLine(dl, g.id);
        }

        // (2) Per-line classification for settlement events that have
        // at least one matched / quantity_mismatch line. Fully-orphan
        // GS events are skipped — their total_amount was attributed
        // above.
        const everyUntagged = g.settlementLines.every((l) => !l.resourceCategory);
        const seById = new Map<string, MatchGroupLine>();
        for (const sl of g.settlementLines) seById.set(sl.lineId, sl);
        // HOF-215 — used to ask the price index under the DELIVERY side's
        // product text for an uncredited settlement line. See
        // ``addGutschriftFehltLine``.
        const dlById = new Map<string, MatchGroupLine>();
        for (const dl of g.deliveryLines) dlById.set(dl.lineId, dl);

        for (const sl of g.settlementLines) {
          if (fullyOrphanSettlementEvents.has(sl.eventId)) continue;
          if (sl.matchStatus === 'matched') {
            // Clean line pair inside a partial group → contributes to
            // Vollständig. ``amount`` is the authoritative line gross;
            // fall back to qty × unit_price when the extractor stored
            // only the per-unit fields (some Frutania templates do).
            //
            // #1291 — no ``> 0`` clamp: negative settlement lines
            // (corrections / Nachbelastungen, e.g. Wiesenhof GS #219098
            // −2 160 €) FOLD INTO the sum. Under N:1 the net of all
            // lines settling a delivery line is exactly what
            // "Vollständig" means; clamping at 0 hid corrections.
            const lineMoney =
              sl.amount != null && Number.isFinite(sl.amount)
                ? sl.amount
                : sl.quantity != null &&
                    sl.unitPrice != null &&
                    Number.isFinite(sl.quantity * sl.unitPrice)
                  ? sl.quantity * sl.unitPrice
                  : null;
            if (lineMoney != null) {
              matchedMoney += lineMoney;
            }
            continue;
          }
          if (sl.matchStatus === 'price_missing') {
            // HOF-215 AC 7 — delivered, paired, and credited at NOTHING. Its own
            // ``amount`` is zero by definition, so pricing it needs the shared
            // anchor index, exactly as an uncredited Lieferschein line does —
            // and "Gutschrift fehlt" is precisely what it is. Routed through
            // ``addGutschriftFehltLine`` so it inherits the no-anchor fallback
            // (counted, not priced) and the ``estimate: true`` flag the tile
            // already prints.
            //
            // Counted ONCE: the delivery line it pairs to is ``price_missing``
            // too (worst-of), so branch (1) skips it as non-``unmatched`` and
            // its event is no longer fully orphan after the fix above.
            //
            // Asked under the DELIVERY side's product text first (codex round 2,
            // P1): that is how Tier-A anchors are keyed, and the two documents
            // routinely word the same product differently — pairing on quantity
            // alone is the whole point of the matcher. The line's own name is
            // the fallback, which is what Tier-B (orphan-settlement) anchors are
            // keyed by.
            const anchorNames = sl.matchedLineIds
              .map((id) => dlById.get(id)?.productName)
              .filter((n): n is string => !!n);
            addGutschriftFehltLine(sl, g.id, [...anchorNames, sl.productName]);
            continue;
          }
          if (sl.matchStatus === 'unmatched') {
            if (!everyUntagged && sl.resourceCategory !== 'goods') continue;
            if (sl.amount != null && Number.isFinite(sl.amount)) {
              lieferscheinFehltMoney += sl.amount;
            }
          }
          // ``quantity_mismatch`` is handled below via the delivery-line
          // iteration so the overlap + delta math reads against the
          // paired LS line in one place.
        }

        // (3) Quantity mismatches: split each qty-mismatch bipartite
        // COMPONENT (#1391 N:M generalisation of the #1291 per-delivery
        // split). A component is a cluster of qty_mismatch lines
        // connected through ``matchedLineIds`` — it covers 1:1, 1:N
        // (delivery fan-out, #1291) AND N:1 (settlement fan-out, #1391)
        // uniformly. Computing the split per component (not per delivery
        // line) attributes every line's quantity EXACTLY ONCE: a
        // settlement line shared by N delivery lines would otherwise be
        // double-counted by a per-delivery loop. The matched overlap →
        // Vollständig; the delta on whichever side overran → the
        // corresponding fehlt. unit_price lives authoritatively on the
        // settlement side; with several settlement lines at mixed prices
        // the split uses the qty-weighted effective price. qty_mismatch
        // lines can't exist on a fully-orphan event by definition.
        const deById = new Map<string, MatchGroupLine>();
        for (const dl of g.deliveryLines) deById.set(dl.lineId, dl);
        const visitedQm = new Set<string>();
        for (const seed of g.deliveryLines) {
          if (seed.matchStatus !== 'quantity_mismatch') continue;
          if (visitedQm.has(seed.lineId)) continue;
          // BFS the bipartite qty-mismatch component reachable from the
          // seed delivery line. ``unresolved`` flips when any counterpart
          // id (or a fold-consumed carrier) doesn't resolve to a rendered
          // line — mirrors the pre-#1391 per-line skip (a SUBSET sum
          // would report phantom fehlt money).
          const compDelivery: MatchGroupLine[] = [];
          const compSettlement: MatchGroupLine[] = [];
          const seenSettlement = new Set<string>();
          const queue: MatchGroupLine[] = [seed];
          visitedQm.add(seed.lineId);
          let unresolved = false;
          while (queue.length > 0) {
            const dl = queue.shift() as MatchGroupLine;
            compDelivery.push(dl);
            if (dl.matchedLineIds.length === 0) unresolved = true;
            for (const sid of dl.matchedLineIds) {
              const sl = seById.get(sid);
              if (sl === undefined) {
                unresolved = true;
                continue;
              }
              if (seenSettlement.has(sl.lineId)) continue;
              seenSettlement.add(sl.lineId);
              compSettlement.push(sl);
              // Walk back to every delivery line this settlement line
              // settles so an N:1 (or N:M) cluster is fully gathered.
              for (const did of sl.matchedLineIds) {
                const peer = deById.get(did);
                if (peer === undefined) {
                  unresolved = true;
                  continue;
                }
                if (peer.matchStatus !== 'quantity_mismatch') continue;
                if (visitedQm.has(peer.lineId)) continue;
                visitedQm.add(peer.lineId);
                queue.push(peer);
              }
            }
          }
          if (unresolved || compSettlement.length === 0) continue;
          // #1305 — split on EFFECTIVE (kolli-folded) quantities, the
          // same rule the hook's aggregate pass uses. ``unitPrice`` stays
          // RAW (per-unit) — only the quantity terms become effective.
          const dlQtys = compDelivery.map((dl) => effectiveLineQty(dl));
          const slQtysRaw = compSettlement.map((sl) => effectiveLineQty(sl));
          // Quantities decide the overage DIRECTION; a null quantity means we
          // can't even tell which side overran, so skip the component.
          if (dlQtys.some((q) => q == null) || slQtysRaw.some((q) => q == null)) {
            continue;
          }
          const dlQty = (dlQtys as number[]).reduce((acc, q) => acc + q, 0);
          const slQtys = slQtysRaw as number[];
          const slQtySum = slQtys.reduce((acc, q) => acc + q, 0);
          // #2138 codex — an LS overage (delivered more than credited) is a
          // genuine "Gutschrift fehlt" off the quantity DIRECTION alone: a
          // smaller credited quantity, a zero-credit settlement, and an
          // unpriceable amount-only line all still owe a Gutschrift. Record it
          // ONCE here — BEFORE the zero-sum and unit-price early returns —
          // tagging the group for Y and counting the gap line for the tile's
          // "kein Preisanker" fallback (mirrors ``addGutschriftFehltLine``,
          // which counts the line regardless of price). Missing this let an
          // under-credited Lieferschein read as "abgerechnet". Money is booked
          // only on the priced path below, so Z stays byte-identical.
          const lsOverage = dlQty > slQtySum;
          if (lsOverage) {
            gsFehltGroupIds.add(g.id);
            gsFehltLines++;
          }
          // A zero-credit settlement has no per-unit split (its weighted price
          // would divide by zero); an amount-only line has no per-unit price.
          // Both skip the money split — the gap is already recorded above.
          if (slQtySum === 0) continue;
          if (compSettlement.some((sl) => sl.unitPrice == null || !Number.isFinite(sl.unitPrice))) {
            continue;
          }
          // Qty-weighted effective price; the single-settlement shortcut
          // keeps the 1:1 / N:1 reduction EXACT (no (q×p)/q float wobble).
          const effPrice =
            compSettlement.length === 1
              ? (compSettlement[0].unitPrice as number)
              : compSettlement.reduce(
                  (acc, sl, i) => acc + slQtys[i] * (sl.unitPrice as number),
                  0,
                ) / slQtySum;
          // Overlap = the quantity both sides agree on → Vollständig.
          // #1291 — negatives fold in (no ``> 0`` clamp).
          const overlapValue = Math.min(dlQty, slQtySum) * effPrice;
          if (Number.isFinite(overlapValue) && overlapValue !== 0) {
            matchedMoney += overlapValue;
          }
          if (lsOverage) {
            // LS overage priced: book the euros. The gap line + Y tag were
            // recorded above; ``gsFehltCovered`` marks this one as priced.
            const owed = (dlQty - slQtySum) * effPrice;
            if (Number.isFinite(owed) && owed > 0) {
              gutschriftFehltMoney += owed;
              gsFehltCovered++;
            }
          } else if (slQtySum > dlQty) {
            // GS overage: credited more than delivered → Lieferschein fehlt.
            const owed = (slQtySum - dlQty) * effPrice;
            if (Number.isFinite(owed) && owed > 0) {
              lieferscheinFehltMoney += owed;
            }
          }
        }

        // #1311 — this partial group's net push into either fehlt bucket
        // is its Abweichung contribution (a lens over the same euros, not
        // a separate accumulation — the fehlt totals keep them too).
        abweichungMoney +=
          (lieferscheinFehltMoney - lsBefore) + (gutschriftFehltMoney - gsBefore);
        break;
      }
    }
  }

  return {
    matchedMoney,
    lieferscheinFehltMoney,
    gutschriftFehltMoney,
    gsFehltCovered,
    gsFehltLines,
    gsFehltGroupIds,
    abweichungMoney,
  };
}

interface FilterBarProps {
  statusFilter: string;
  onStatusChange: (v: string) => void;
  groups: MatchGroup[];
  // Optimistic operator-disposition overrides keyed by the orphan
  // side's entity id (settlement_event for orphan_settlement,
  // delivery_event for orphan_delivery). Layered on top of the
  // persisted ``manuallyResolvedAt`` attribute. Lets the dashboard's
  // "needs attention" counts react instantly when the kebab fires
  // without waiting for the worker to drain its buffer.
  resolvedOverrides?: ReadonlyMap<string, boolean>;
  // #1394 — the editor-exit nav link, hosted in the tiles/header panel
  // so both views (feed → editor, editor → overview) expose it from the
  // same place. Optional: when absent (e.g. the FilterBar.tiles unit
  // test) no <Link> renders, so the bar needs no Router context.
  navLink?: { to: string; label: string };
  // #1511 — clicking the leading "Gesamt" tile is "show me everything".
  // On the feed page that means BOTH navigate to the whole-season view
  // (``?ym=gesamt``) AND clear the status filter — so a user who drilled
  // into a single month can get back to the season roll-up from the UI
  // (the old sidebar Gesamt lens used to be that path; codex r1 P2). When
  // this callback is absent (the canvas/editor host + the tiles unit
  // test, neither of which has a season view), the tile falls back to
  // just clearing the status filter via ``onStatusChange('')``.
  onSelectGesamt?: () => void;
  // #2113 — opt-in plain-language summary sentence above the tiles.
  // Absent/false (the DEFAULT, and what the /editor canvas host passes)
  // renders no sentence: /editor is a data-administration surface where
  // a farmer-facing sentence is noise. Only the farmer-facing overview
  // (``GutschriftCheckFeedPage``) opts in. The feed also uses this to
  // suppress the sentence while loading or after a partial load — a
  // confidently-wrong sentence over half-loaded data is worse than none
  // (the bar itself still renders; see the dashboard comment below).
  showSummary?: boolean;
}

export default function FilterBar({
  statusFilter,
  onStatusChange,
  groups,
  resolvedOverrides,
  navLink,
  onSelectGesamt,
  showSummary,
}: FilterBarProps) {
  // The dashboard counts represent "what still needs attention". A
  // group drops from its counter when the operator marks it handled
  // via the kebab — symmetric with whichever tile sides expose the
  // action:
  //   - orphan_settlement → only the settlement side has a kebab; read
  //     resolution off the settlement entity
  //   - orphan_delivery   → only the delivery side has a kebab
  //   - partial           → every card has its own kebab and its own
  //     ``manually_resolved_at``. Per-side accounting:
  //       * marking the GS side removes the GS's full
  //         ``total_amount`` from ``Lieferschein fehlt`` (the side it
  //         contributes to) but LEAVES the LS side's contribution to
  //         ``Gutschrift fehlt`` alone.
  //       * marking the LS side removes the LS lines' fuzzy-priced
  //         contribution from ``Gutschrift fehlt`` but leaves the GS
  //         side alone.
  //       * the group leaves the dashboard entirely only when BOTH
  //         sides are fully resolved.
  //     Earlier ``either-side-resolves`` rule was misleading: marking
  //     GS #999967 also dropped LF20251020's contribution from
  //     ``Gutschrift fehlt`` despite the LS not being marked.
  //   - multi-event partials (1:N like 999960+999959 → LF99990014)
  //     fold into the same rule — each event on each side is checked
  //     independently; the side is "fully resolved" only when every
  //     event on it has been marked.
  // ``isEventResolved`` / ``isOperatorResolved`` extracted to
  // ``resolvedExclusion.ts`` (#1289 C3) so the feed view applies the
  // SAME exclusion and feed rows agree with the tile counts. This
  // component threads its optimistic ``resolvedOverrides`` prop
  // through; the feed passes none (persisted-attribute path).
  const eventResolved = (ev: Record<string, unknown> | null | undefined): boolean =>
    isEventResolved(ev, resolvedOverrides);
  // Strip resolved events from a group so per-event dashboard math
  // ignores their contribution. The primary ``settlementEvent`` /
  // ``deliveryEvent`` (read by single-event code paths like the
  // ``matched`` and ``orphan_*`` branches in ``computeDashboardTotals``)
  // are also reseated onto the first unresolved event of their side
  // — otherwise resolving the lex-smallest primary would leave a
  // dangling reference whose ``total_amount`` still flowed into the
  // totals.
  //
  // #2154 — after filtering the resolved side's lines, rederive the
  // SURVIVING lines' pairings (shared ``rederiveLinePairs``, the same
  // helper ``excludeSuppressedEvents`` uses): a ``quantity_mismatch`` line
  // whose every counterpart was resolved away must flip to ``unmatched``,
  // else it dangles at a now-removed counterpart and ``computeDashboardTotals``
  // §3 skips it (``seById`` miss → ``unresolved`` → ``continue``), dropping a
  // genuine per-side gap from the totals (a GS-side-resolved LS overage read
  // "abgerechnet"; the symmetric LS-side gap silently vanished). This MOVES
  // money by design — the full surviving quantity books to the fehlt bucket.
  // Unlike ``excludeSuppressedEvents`` we deliberately do NOT rederive the
  // group ``status``: a per-side-resolved partial stays a ``partial`` (it
  // keeps its place in the Abweichung count), so ``brokeAPair`` /
  // ``partialPrune`` are ignored here.
  const stripResolvedEvents = (g: MatchGroup): MatchGroup => {
    const resolvedSeIds = new Set<string>();
    for (const ev of g.allSettlementEvents) {
      const id = String(ev.entity_id ?? '');
      if (id && eventResolved(ev)) resolvedSeIds.add(id);
    }
    const resolvedDeIds = new Set<string>();
    for (const ev of g.allDeliveryEvents) {
      const id = String(ev.entity_id ?? '');
      if (id && eventResolved(ev)) resolvedDeIds.add(id);
    }
    if (resolvedSeIds.size === 0 && resolvedDeIds.size === 0) return g;
    const allSe = g.allSettlementEvents.filter(
      (e) => !resolvedSeIds.has(String(e.entity_id ?? '')),
    );
    const allDe = g.allDeliveryEvents.filter(
      (e) => !resolvedDeIds.has(String(e.entity_id ?? '')),
    );
    const seLines = g.settlementLines.filter((l) => !resolvedSeIds.has(l.eventId));
    const deLines = g.deliveryLines.filter((l) => !resolvedDeIds.has(l.eventId));
    // #2154 — the precise set of line ids dropped because their EVENT was
    // resolved, built from the ORIGINAL line arrays (mirror of
    // ``excludeSuppressedEvents``; NEVER "absent from the surviving set", or a
    // folded-away packaging-carrier counterpart would be mistaken for a
    // vanished one — codex #1436 P2). ``rederiveLinePairs`` prunes surviving
    // ``matchedLineIds`` against THIS set and flips a fully-orphaned line to
    // ``unmatched``.
    const removedLineIds = new Set<string>();
    for (const l of g.settlementLines) {
      if (resolvedSeIds.has(l.eventId)) removedLineIds.add(l.lineId);
    }
    for (const l of g.deliveryLines) {
      if (resolvedDeIds.has(l.eventId)) removedLineIds.add(l.lineId);
    }
    const { settlementLines: rederivedSeLines, deliveryLines: rederivedDeLines } =
      rederiveLinePairs(seLines, deLines, removedLineIds);
    const primarySeId = String(
      (g.settlementEvent as Record<string, unknown> | null)?.entity_id ?? '',
    );
    const primaryDeId = String(
      (g.deliveryEvent as Record<string, unknown> | null)?.entity_id ?? '',
    );
    const settlementEvent =
      primarySeId && resolvedSeIds.has(primarySeId)
        ? (allSe[0] as MatchGroup['settlementEvent']) ?? null
        : g.settlementEvent;
    const deliveryEvent =
      primaryDeId && resolvedDeIds.has(primaryDeId)
        ? (allDe[0] as MatchGroup['deliveryEvent']) ?? null
        : g.deliveryEvent;
    return {
      ...g,
      allSettlementEvents: allSe,
      allDeliveryEvents: allDe,
      settlementLines: rederivedSeLines,
      deliveryLines: rederivedDeLines,
      settlementEvent,
      deliveryEvent,
    };
  };
  const dashboardGroups = groups
    .filter((g) => !isOperatorResolved(g, resolvedOverrides))
    .map(stripResolvedEvents);

  // Derive unique Kunden from group entities
  const kunden = Array.from(
    new Set(
      groups
        .map((g) => {
          const org =
            (g.settlementEvent?.supplier_name as string | undefined) ??
            (g.deliveryEvent?.supplier_name as string | undefined);
          return org ?? null;
        })
        .filter((k): k is string => k !== null),
    ),
  );

  // Codex round-3: surface status counts so the weekly reviewer sees workload
  // at a glance without scanning the canvas. ``dashboardGroups`` already
  // drops operator-resolved orphans so the counters reflect remaining work.
  const counts = dashboardGroups.reduce(
    (acc, g) => {
      acc[g.status] = (acc[g.status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const totals = computeDashboardTotals(dashboardGroups);

  // #1311 — counts are pure per-status so a tile's count === the rows its
  // click reveals (the feed filters by ``g.status === value``). Partial
  // groups land in Abweichung only; they no longer inflate the fehlt
  // tiles (D1, reverses the 2026-05-16 folding that existed because the
  // Abweichung tile was removed).
  const gsFehltCount = counts.orphan_delivery ?? 0;
  const lsFehltCount = counts.orphan_settlement ?? 0;
  const abweichungCount = counts.partial ?? 0;
  const matchedCount = counts.matched ?? 0;

  // #1511 — leading "Gesamt" tile. The count is the number of delivery
  // groups across every status (the four buckets are mutually exclusive
  // and exhaustive, so the sum === dashboardGroups.length). The grand
  // total € is the NON-OVERLAPPING coverage of the month:
  //   matchedMoney + gutschriftFehltMoney + lieferscheinFehltMoney
  // ``abweichungMoney`` is DELIBERATELY excluded: it is a per-category
  // LENS over euros that already live in the two *fehlt* buckets (see the
  // ``DashboardTotals.abweichungMoney`` doc + the snapshot-delta in
  // ``computeDashboardTotals``), so a naive 4-way sum would double-count
  // every partial-group euro. matched + both fehlt is the near-complete
  // per-euro coverage of the month (modulo the document-level
  // Kommission / Skonto / MWSt fees both branches intentionally skip).
  const gesamtCount = dashboardGroups.length;
  const gesamtMoney =
    totals.matchedMoney + totals.gutschriftFehltMoney + totals.lieferscheinFehltMoney;

  // ── #2113 — the plain-language summary sentence ──────────────────────
  //
  // Computed HERE (not in the host page) on purpose: it reads the exact
  // same ``dashboardGroups`` / ``counts`` / ``totals`` the tiles read, so
  // sentence and tiles cannot drift. ``stripResolvedEvents`` above is a
  // private closure — a page-level implementation would have to duplicate
  // its 54 lines and would silently keep operator-resolved events that
  // the tiles have already dropped.
  //
  //   X = distinct LS DOCUMENTS across ``dashboardGroups``. NOT
  //       ``gesamtCount``: that counts groups across every status
  //       INCLUDING ``orphan_settlement``, which carry no Lieferschein at
  //       all. Not available from the hook either (``gsCountsByMonth`` &
  //       co. are distinct SETTLEMENT ids, and unfiltered by
  //       suppression/resolution).
  //   Y = orphan_delivery groups + the partials that fed a GS-side gap
  //       (``totals.gsFehltGroupIds`` — #2138) — "Lieferscheine ohne
  //       vollständige Gutschrift". WIDER than the "Gutschrift fehlt"
  //       tile's count rendered directly below (that count is pure per-
  //       status, #1311 D1 — a partial lands in Abweichung only), because
  //       ``gutschriftFehltMoney`` (= Z) folds partial-group LS gaps and
  //       qty-mismatch LS overage in. But Y is now EXACTLY Z's population,
  //       not "every partial": #2138 dropped pure OVER-credit partials
  //       (``slQtySum > dlQty`` → Lieferschein-fehlt only, 0 to Z) from Y,
  //       since counting them made "das entspricht" claim a missing
  //       Gutschrift for a fully-credited Lieferschein. The invariant
  //       ``Y > 0 ⟺ Z > 0`` now holds. Accepted cost: on a month with
  //       partials, Y still does not equal the orphan_delivery tile's
  //       count. Z stays byte-identical to that tile.
  //   Z = ``totals.gutschriftFehltMoney`` via the shared ``eurFmt``,
  //       which ALREADY appends the € sign (and an NBSP before it) — do
  //       NOT append another.
  //
  // The X/Y unit mismatch (documents vs groups) is intentional and reads
  // correctly: a group is one connected component ≈ one expected
  // Gutschrift, so a 2-LS component says "Von 2 Lieferscheinen fehlt noch
  // 1 Gutschrift".
  const deliveryEventsOf = (g: MatchGroup): Record<string, unknown>[] =>
    g.allDeliveryEvents.length > 0
      ? g.allDeliveryEvents
      : g.deliveryEvent
        ? [g.deliveryEvent as Record<string, unknown>]
        : [];
  // A Set, not a sum: cheap insurance against one delivery event id
  // surfacing in two groups (cf. the #1389 cross-month duplicate-id
  // hazard the hook documents for settlement ids).
  const lsDocIds = new Set<string>();
  for (const g of dashboardGroups) {
    for (const ev of deliveryEventsOf(g)) {
      const id = String(ev.entity_id ?? '');
      if (id) lsDocIds.add(id);
    }
  }
  const lsDocCount = lsDocIds.size;
  // #2138 — Y is EXACTLY Z's population: orphan_delivery groups + the
  // partials that fed a GS-side gap (``totals.gsFehltGroupIds``). Reading
  // the set back — rather than re-classifying partials here — keeps Y and Z
  // from ever drifting (the #2113 manifest's duplication trap), and the
  // intersection runs over the same ``dashboardGroups`` the totals did. A
  // pure over-credit partial contributes 0 to Z and is absent from the set,
  // so it no longer inflates Y; the result is ``Y > 0 ⟺ Z > 0``.
  const partialGsFehltCount = dashboardGroups.filter(
    (g) => g.status === 'partial' && totals.gsFehltGroupIds.has(g.id),
  ).length;
  const offeneGsCount = gsFehltCount + partialGsFehltCount;

  const summaryText = ((): string | null => {
    // Empty month (or a GS-only month): the bar still renders its zeros,
    // but the feed's own "Keine Lieferscheine oder Gutschriften für
    // <Monat>." empty state owns the message. A "Von 0 Lieferscheinen…"
    // sentence would duplicate and contradict it.
    if (lsDocCount === 0) return null;
    // Nothing outstanding — the state the product is steering the user
    // TOWARD, so it gets the good sentence rather than "fehlen noch 0
    // Gutschriften … ca. 0 €".
    if (offeneGsCount === 0) {
      return lsDocCount === 1
        ? 'Der Lieferschein ist abgerechnet.'
        : `Alle ${lsDocCount} Lieferscheine sind abgerechnet.`;
    }
    // Four grammatical branches: "Von 1 Lieferschein" is the dative
    // SINGULAR (the ``-en`` in "Lieferscheinen" is the dative plural),
    // and the verb inflects with Y independently of X.
    const head =
      `Von ${lsDocCount} Lieferschein${lsDocCount === 1 ? '' : 'en'} ` +
      `${offeneGsCount === 1 ? 'fehlt' : 'fehlen'} noch ${offeneGsCount} ` +
      `Gutschrift${offeneGsCount === 1 ? '' : 'en'}`;
    // No price anchor anywhere in the month → the tile prints "kein
    // Preisanker" instead of a figure; "ca. 0 €" here would be a lie, so
    // the money clause is dropped rather than zeroed. Partial coverage
    // (gsFehltCovered < gsFehltLines) keeps the clause — "ca." hedges it,
    // exactly as the tile does.
    if (totals.gsFehltCovered === 0) return `${head}.`;
    return (
      `${head} – das entspricht einem offenen Betrag von ` +
      `ca. ${eurFmt.format(totals.gutschriftFehltMoney)}.`
    );
  })();
  const summary = showSummary ? summaryText : null;

  // Each tile is the filter control: clicking toggles the status filter
  // on, clicking the active tile again clears it.
  const toggle = (value: string) => () =>
    onStatusChange(statusFilter === value ? '' : value);

  return (
    <div className="filter-bar">
      {/* #2113 — the summary sentence leads the bar (``flex: 1 0 100%``
          puts it on its own row above the tiles, using the flex-wrap the
          bar already has, so ``filter-bar__info`` / ``filter-bar__nav``
          keep their ``margin-left: auto`` placement on the tiles' row).
          ``role="status"`` announces the recomputed sentence; it sits
          OUTSIDE the aria-label="Abgleich-Übersicht" dashboard group so
          it isn't read as part of the tile list. */}
      {summary && (
        <p className="filter-bar__summary" role="status">
          {summary}
        </p>
      )}
      {/*
        Dashboard tiles render unconditionally — even on an empty month
        (no LS / no GS yet) the reviewer should see the three buckets
        with explicit zeros, so the reading is "April: nothing to do"
        rather than "did the dashboard fail to load?". Neutral accent
        on zero counts keeps the visual quiet.
      */}
      <div className="filter-bar__dashboard" aria-label="Abgleich-Übersicht">
          {/* #1511 — leading "Gesamt" tile: total delivery-group count +
              the non-overlapping grand total € (matched + both fehlt; the
              Abweichung lens is excluded — it would double-count euros
              already in the fehlt buckets). Default-selected when no
              status filter is set; clicking it clears any filter so the
              feed returns to the whole set. The grand total is exact
              (matched ``total_amount`` + orphan ``total_amount`` +
              fuzzy-priced fehlt lines), so it renders WITHOUT the "ca."
              prefix even though some fehlt components are estimated — the
              estimate disclosure lives on the per-status fehlt tiles. */}
          <Tile
            accent="total"
            label="Gesamt"
            count={gesamtCount}
            isActive={statusFilter === ''}
            // #1511 codex r1 P2 — prefer the host's "show me everything"
            // handler (feed: navigate to ``?ym=gesamt`` + clear the
            // filter, restoring the season roll-up the removed sidebar
            // lens used to reach). Fall back to clearing the status filter
            // when no season view exists (canvas/editor host, unit test).
            onClick={onSelectGesamt ?? (() => onStatusChange(''))}
            money={gesamtMoney > 0 ? { value: gesamtMoney } : null}
            moneyEmpty={gesamtMoney > 0 ? undefined : '—'}
            moneyTitle="Gesamtzahl der Liefergruppen + Gesamtbetrag (vollständig abgeglichen + fehlende GS/LS, ohne Doppelzählung der Abweichung)"
          />
          {/* Abweichung tile re-introduced (#1311) — counts are pure per-status so tile count === click-filter result. */}
          {/* Severity reconciled with #1289: "Gutschrift fehlt" (goods
              delivered but not credited — money owed to the farmer) is
              the critical bucket → red alarm accent; "Lieferschein
              fehlt" stays the amber warn. */}
          <Tile
            accent={gsFehltCount > 0 ? 'alarm' : 'neutral'}
            label="Gutschrift fehlt"
            count={gsFehltCount}
            isActive={statusFilter === 'orphan_delivery'}
            onClick={toggle('orphan_delivery')}
            money={
              totals.gsFehltCovered > 0
                ? { value: totals.gutschriftFehltMoney, estimate: true }
                : null
            }
            moneyEmpty={
              gsFehltCount === 0 && totals.gsFehltLines === 0
                ? '—'
                : totals.gsFehltCovered === 0
                  ? 'kein Preisanker'
                  : undefined
            }
            moneyTitle={
              totals.gsFehltCovered > 0 && totals.gsFehltCovered < totals.gsFehltLines
                ? `Preis nur für ${totals.gsFehltCovered} von ${totals.gsFehltLines} Positionen vorhanden — inkl. offener LS-Positionen aus unvollständig abgeglichenen Paaren`
                : 'Geschätzter Wert offener LS-Positionen — fehlende GS-Belege + offene LS-Positionen aus unvollständig abgeglichenen Paaren'
            }
          />
          {/* Abweichung (#1311) — partial groups (one side of the pair
              has a line-level gap). Count is pure ``counts.partial`` so a
              click reveals exactly these rows; money is the partial-branch
              flow into the fehlt buckets (a lens, "ca." because the
              LS-side portion is fuzzy-priced), disclosed as overlapping
              in the tooltip. */}
          <Tile
            accent={abweichungCount > 0 ? 'alarm' : 'neutral'}
            label="Abweichung"
            count={abweichungCount}
            isActive={statusFilter === 'partial'}
            onClick={toggle('partial')}
            money={
              totals.abweichungMoney > 0
                ? { value: totals.abweichungMoney, estimate: true }
                : null
            }
            moneyEmpty={
              abweichungCount === 0 && totals.abweichungMoney === 0 ? '—' : 'kein Betrag'
            }
            moneyTitle="Offene Beträge aus unvollständig abgeglichenen Paaren (beide Richtungen) — dieselben Beträge fließen auch in die Fehlt-Kacheln ein"
          />
          <Tile
            accent={lsFehltCount > 0 ? 'warn' : 'neutral'}
            label="Lieferschein fehlt"
            count={lsFehltCount}
            isActive={statusFilter === 'orphan_settlement'}
            onClick={toggle('orphan_settlement')}
            money={
              totals.lieferscheinFehltMoney > 0
                ? { value: totals.lieferscheinFehltMoney }
                : null
            }
            moneyEmpty={
              lsFehltCount === 0 && totals.lieferscheinFehltMoney === 0
                ? '—'
                : totals.lieferscheinFehltMoney > 0
                  ? undefined
                  : 'kein Betrag'
            }
            moneyTitle="Gutschriftbetrag ohne LS-Beleg — inkl. offener GS-Positionen aus unvollständig abgeglichenen Paaren"
          />
          <Tile
            accent={matchedCount > 0 ? 'ok' : 'neutral'}
            label="Vollständig"
            count={matchedCount}
            isActive={statusFilter === 'matched'}
            onClick={toggle('matched')}
            money={totals.matchedMoney > 0 ? { value: totals.matchedMoney } : null}
            moneyEmpty={totals.matchedMoney > 0 ? undefined : '—'}
            moneyTitle="Summe vollständig abgeglichener Belege + abgeglichene Positionen aus unvollständig abgeglichenen Paaren"
          />
      </div>

      {kunden.length > 0 && (
        <span className="filter-bar__info">
          {kunden.length} Lieferant{kunden.length !== 1 ? 'en' : ''}
        </span>
      )}

      {/* #1394 — editor-exit nav link. ``filter-bar__nav`` carries
          ``margin-left: auto`` so it sits at the bar's trailing edge; when
          ``filter-bar__info`` is also present the info span keeps its own
          auto-margin and the link trails it. */}
      {navLink && (
        <Link className="filter-bar__nav" to={navLink.to}>
          {navLink.label}
        </Link>
      )}
    </div>
  );
}
